<template>
  <div class="app-wrap" data-qa="patient-make-payment-page">
    <div v-if="loading" class="loading-wrapper">
      <updox-loading color="#003462" size="80px" />
    </div>
    <div v-else-if="error">
      <updox-error :error-details="errorDetails" />
    </div>
    <div v-else-if="paymentDetailStatus === 'PAYMENT_INVALID_STATE'">
      <section class="main-header">
        <div class="primary-header page-wrapper flex-column align-center">
          <h2>{{ paymentDetails.practiceName }}</h2>
          <small>{{ paymentDetails.practicePhone }}</small>
        </div>
      </section>
      <section class="page-wrapper">
        <updox-payment-invalid-state
          :invalid-state="paymentDetails.invalidState"
        />
      </section>
    </div>
    <div v-else-if="paymentDetailStatus === 'TOKEN_NOT_FOUND'">
      <updox-bad-payment-request-token />
    </div>
    <div v-else-if="paymentDetailStatus === 'PRACTICE_UNABLE_TO_PROCESS'">
      <updox-cannot-process-payments />
    </div>
    <div v-else>
      <section class="main-header">
        <div class="primary-header page-wrapper flex-column align-center">
          <h2>{{ paymentDetails.practiceName }}</h2>
          <small>{{ paymentDetails.practicePhone }}</small>
        </div>
      </section>
      <section class="page-wrapper">
        <h3>Make a payment</h3>
        <payment-remit-form
          :forms-data="loadedForm"
          :payment-details="paymentDetails"
          v-on:parent-status="setPreverificationStatus"
          v-on:parent-error="setError"
        />
      </section>
    </div>

    <updox-footer />
  </div>
</template>
<script>
  import PaymentRemitForm from "../components/PaymentRemitForm";
  import { UpdoxLoading } from "@updox/common-vue";
  import UpdoxBadPaymentRequestToken from "@/components/common/UpdoxBadPaymentRequestToken";
  import UpdoxCannotProcessPayments from "@/components/common/UpdoxCannotProcessPayments";
  import UpdoxPaymentInvalidState from "@/components/common/UpdoxPaymentInvalidState";
  import UpdoxError from "@/components/common/UpdoxError";
  import UpdoxFooter from "../components/common/UpdoxFooter";
  import SimplePaymentsPatientService from "../services/SimplePaymentsPatientService";
  import { formatUpdoxPhone } from "@/utils/formatting";

  export default {
    name: "PaymentRemit",
    components: {
      PaymentRemitForm,
      UpdoxLoading,
      UpdoxBadPaymentRequestToken,
      UpdoxCannotProcessPayments,
      UpdoxPaymentInvalidState,
      UpdoxError,
      UpdoxFooter,
    },
    data: () => ({
      loading: true,
      error: false,
      errorDetails: null,
      paymentDetailStatus: null,
      // This is borrowing from a pattern updox/forms uses; for client-side only forms, we can mock the data and make reuse of all of the
      // pre-built ux. Things like field validation, submit ui state, and error handling.
      loadedForm: {
        fields: [
          {
            type: "currency",
            label: "Amount",
            id: "amount",
            placeholder: "Enter amount",
            required: true,
            // CurrencyField v-model
            value: "",
          },
        ],
      },
    }),
    async mounted() {
      const { paymentRequestToken } = this.$route.params;

      await SimplePaymentsPatientService.getPaymentDetails(paymentRequestToken)
        .then((response) => {
          console.debug("getPaymentDetails response - ", response);

          this.paymentDetailStatus = response?.status;

          const paymentDetails = {
            amount: response?.amount,
            preventPartial: response?.preventPartial,
            minAmount: response?.minAmount,
            merchantKey: response?.merchantKey,
            practiceName: response?.practiceName,
            practicePhone: formatUpdoxPhone(response?.practicePhone),
            reason: response?.reason,
            requestDate: response?.paymentRequestDate,
            status: response?.status,
            invalidState: response?.invalidState,
            termsAndConditionsLink: response?.termsAndConditionsLink,
            updoxPatientTermsOfServiceUrl:
              response?.updoxPatientTermsOfServiceUrl,
            updoxPatientPrivacyLink: response?.updoxPatientPrivacyLink,
          };
          // Pre-fill the Amount field
          this.loadedForm.fields[0].value = response?.amount;

          // The idea is to get the response paymentDetails, immediately set them to $store and then get the
          // computed paymentDetails to pass as props to PaymentRemitForm
          this.$store.commit("setPaymentDetails", paymentDetails);
          this.loading = false;

          /*airbrake.addFilter(notice => {
              notice.params.paymentRequestToken = paymentRequestToken;
              notice.params.role = "PaymentRequestToken";
              return notice;
            });*/
        })
        .catch((error) => {
          let {
            response: { status: errorCode },
          } = error;
          if (typeof errorCode == "undefined" || 401 !== errorCode) {
            this.error = true;
            this.loading = false;
            this.errorDetails = error;
            /*airbrake.notify({
              error: e,
              message: "Unable to process payment token"
            });*/
          }
        });
    },
    methods: {
      setPreverificationStatus(status) {
        this.paymentDetailStatus = status;
      },
      setError(e) {
        this.error = true;
        console.log(e);
        this.errorDetails = e;
      },
    },
    computed: {
      // Get the full paymentDetails we $store when we mount() and call getPaymentDetails
      paymentDetails() {
        console.debug(
          "$store paymentDetails ",
          this.$store.getters.paymentDetails
        );
        return this.$store.getters.paymentDetails;
      },
    },
  };
</script>
<style scoped lang="scss">
/*We need to import the global styles for each view in order for the compiled web component to inherit the global and updox-styles styling*/
@import "@/assets/_scss/main";

  .loading-wrapper {
    padding-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .page-wrapper {
    margin: 0 auto;
    width: 300px;

    h3 {
      font-size: 18px;
      font-weight: 600;
    }
  }

  .main-header {
    position: relative;
    color: #222;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    z-index: 101;
    width: 100%;

    .primary-header {
      position: relative;
      text-align: center;

      h2 {
        font-weight: 600;
        margin-bottom: 5px;
      }

      small {
        font-size: 12px;
        margin-bottom: 10px;
      }
    }
  }

  .payment-preview {
    background: rgb(237, 246, 249);
    border: 1px solid rgba(0, 127, 173, 0.2);
    border-radius: 4px;

    .requested-details {
      padding: 10px;

      .flex-column {
        &:last-child {
          text-align: right;
        }
      }

      h2 {
        font-weight: 600;
        margin: 0;
      }

      small {
        color: rgb(68, 68, 68);
        font-size: 12px;
        font-weight: 400;
        font-style: italic;
      }
    }

    .reason {
      font-size: 14px;
      padding: 7px 7px 0;

      p {
        margin: 0;

        &:first-child {
          font-weight: 600;
        }
      }
    }

    .options {
      border-top: 1px solid rgba(0, 127, 173, 0.2);
      margin-top: 10px;

      small {
        color: rgb(68, 68, 68);
        font-size: 10px;
        font-weight: 400;
        font-style: italic;
      }

      .container {
        align-items: center;
        padding: 10px;
      }

      .cards {
        svg {
          height: 30px;
          margin-right: 8px;
          width: 30px;

          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }

  .form-container {
    background-color: #fff;
    padding: 15px;

    p {
      margin-top: 0;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      margin-bottom: 30px;
      margin-top: 30px;

      &:first-child {
        margin-top: 0;
      }
    }

    .form-body {
      margin-bottom: 20px;

      ::placeholder {
        font-size: 12px;
      }
    }

    .form-actions {
      p {
        font-size: 12px;
        margin: 0;
      }
    }
  }
</style>
